import { FC } from 'react';
import { useAtomValue, useStore } from 'jotai';
import { $CurrentArticle } from '@article/shared/atoms/current-article';
import { ArticleInfoSection } from '@article/shared/components/article-info-bar';
import {
  ArticleDetailUtil,
  EArticleClass,
  EArticleUnavailableReason,
  IGalleryArticleDetail,
  IVideoArticleDetail,
} from 'shared/models';
import { WapArticleSection } from '@article/body/main/wap-article';
import { GalleryAuthorSection } from './gallery-author-section';
import { ArticleGallery } from '~/components/article-gallery';
import { ArticleVideoSection } from '@article/body/main/article-video';
import { LargeImageSection } from '@article/body/main/large-image';
import { OriginalPostBanner } from './original-post-banner/index';
import { ENotFoundType, NotFound } from '~/components/not-found';
import { $CurrentArticleRelatedPost } from '@article/shared/atoms/current-article-related-post';
import { ScopedProvider } from '@lemon8/web-app-shared/ssr';
import { $CurrentArticleFold, $CurrentArticleHeightComputed } from '../../shared/atoms/current-article-fold';
import { $CurrentGalleryArticle } from '~/components/article-gallery/atoms';
import { $CurrentVideoArticle } from './article-video/atoms';
import { $HitVideoCTAOpt } from '~/utils/libra/video-cta-opt';

export const ArticleMainSections: FC = () => {
  const store = useStore();
  const currentArticle = useAtomValue($CurrentArticle);
  const isAvailable = ArticleDetailUtil.isArticleAvailable(currentArticle);
  const articleRelatedPost = store.get($CurrentArticleRelatedPost);

  if (!isAvailable) {
    const { unavailableReason } = currentArticle;
    return (
      <NotFound
        type={
          unavailableReason === EArticleUnavailableReason.PRIVATE_ACCOUNT
            ? ENotFoundType.PRIVACY_ACCOUNT
            : ENotFoundType.NOT_FOUND
        }
      />
    );
  }

  const { articleClass } = currentArticle;

  if (articleClass === EArticleClass.GALLERY) {
    const article = useAtomValue($CurrentArticle) as IGalleryArticleDetail;
    return (
      <>
        <ScopedProvider
          atoms={[$CurrentArticleFold, $CurrentArticleHeightComputed]}
          atomsWithHydration={[[$CurrentGalleryArticle, article as IGalleryArticleDetail]]}
        >
          <OriginalPostBanner />
          <GalleryAuthorSection />
          <ArticleGallery
            article={article as IGalleryArticleDetail}
            enableIndexForRecommend={true}
            articleRelatedPosts={articleRelatedPost}
          />
          <ArticleInfoSection />
          <WapArticleSection />
        </ScopedProvider>
      </>
    );
  }

  if (articleClass === EArticleClass.VIDEO) {
    const article = useAtomValue($CurrentArticle) as IVideoArticleDetail;
    const hitVideoCTAOpt = useAtomValue($HitVideoCTAOpt);
    return hitVideoCTAOpt ? (
      <ScopedProvider
        atoms={[$CurrentArticleFold, $CurrentArticleHeightComputed]}
        atomsWithHydration={[[$CurrentVideoArticle, article as IVideoArticleDetail]]}
      >
        <OriginalPostBanner />
        <GalleryAuthorSection />
        <ArticleVideoSection />
        <ArticleInfoSection />
        <WapArticleSection />
      </ScopedProvider>
    ) : (
      <>
        <OriginalPostBanner />
        <ArticleVideoSection />
        <ArticleInfoSection />
      </>
    );
  }

  return (
    <>
      <ScopedProvider atoms={[$CurrentArticleFold, $CurrentArticleHeightComputed]}>
        <OriginalPostBanner />
        <LargeImageSection />
        <WapArticleSection />
      </ScopedProvider>
    </>
  );
};
